import type {
  SiteTheme,
  SiteThemeBorderEmphasis,
  SiteThemeCardRadius,
  SiteThemeCardShadow,
  SiteThemeSpacingScale,
  SiteThemePresentation,
} from '../domains/site/schema';

export type ResolvedSiteThemePresentation = Omit<
  SiteThemePresentation,
  'cardShadow' | 'borderEmphasis' | 'cardRadius' | 'spacingScale'
> & {
  cardShadow: SiteThemeCardShadow;
  borderEmphasis: SiteThemeBorderEmphasis;
  cardRadius: SiteThemeCardRadius;
  spacingScale: SiteThemeSpacingScale;
};

type ResolvedSpacingScaleValues = {
  sectionPaddingY: string;
  sectionGap: string;
  stackGap: string;
  cardPadding: string;
};

const defaultThemePresentation: ResolvedSiteThemePresentation = {
  archetypeId: 'jp_b2b_trust_authority',
  sectionHeadingStyle: 'center_bar',
  cardStyle: 'surface',
  cardShadow: 'none',
  borderEmphasis: 'normal',
  cardRadius: 'rounded',
  spacingScale: 'normal',
  heroLayoutStyle: 'editorial_centered',
  proofLayoutStyle: 'grid',
  reviewLayoutStyle: 'cards',
  ctaLayoutStyle: 'panel',
};

const spacingScaleValues: Record<SiteThemeSpacingScale, ResolvedSpacingScaleValues> = {
  compact: {
    sectionPaddingY: '4rem',
    sectionGap: '1.5rem',
    stackGap: '1.25rem',
    cardPadding: '1.25rem',
  },
  normal: {
    sectionPaddingY: '5rem',
    sectionGap: '2rem',
    stackGap: '1.5rem',
    cardPadding: '1.5rem',
  },
  spacious: {
    sectionPaddingY: '6.5rem',
    sectionGap: '2.5rem',
    stackGap: '1.75rem',
    cardPadding: '1.75rem',
  },
};

export function resolveCardShadow(
  cardStyle: SiteThemePresentation['cardStyle'],
  cardShadow?: SiteThemeCardShadow,
): SiteThemeCardShadow {
  if (cardShadow) return cardShadow;
  return cardStyle === 'elevated' ? 'medium' : 'none';
}

export function resolveBorderEmphasis(
  borderEmphasis?: SiteThemeBorderEmphasis,
): SiteThemeBorderEmphasis {
  return borderEmphasis ?? 'normal';
}

export function resolveCardRadius(
  cardRadius?: SiteThemeCardRadius,
): SiteThemeCardRadius {
  return cardRadius ?? 'rounded';
}

export function resolveSpacingScale(
  spacingScale?: SiteThemeSpacingScale,
): SiteThemeSpacingScale {
  return spacingScale ?? 'normal';
}

export function increaseCardShadow(cardShadow: SiteThemeCardShadow): SiteThemeCardShadow {
  switch (cardShadow) {
    case 'none':
      return 'medium';
    case 'soft':
      return 'medium';
    case 'medium':
      return 'strong';
    case 'strong':
      return 'strong';
  }
}

export function decreaseCardShadow(cardShadow: SiteThemeCardShadow): SiteThemeCardShadow {
  switch (cardShadow) {
    case 'strong':
      return 'medium';
    case 'medium':
      return 'soft';
    case 'soft':
      return 'none';
    case 'none':
      return 'none';
  }
}

export function increaseBorderEmphasis(
  borderEmphasis: SiteThemeBorderEmphasis,
): SiteThemeBorderEmphasis {
  switch (borderEmphasis) {
    case 'none':
      return 'soft';
    case 'soft':
      return 'normal';
    case 'normal':
      return 'strong';
    case 'strong':
      return 'strong';
  }
}

export function decreaseBorderEmphasis(
  borderEmphasis: SiteThemeBorderEmphasis,
): SiteThemeBorderEmphasis {
  switch (borderEmphasis) {
    case 'strong':
      return 'normal';
    case 'normal':
      return 'soft';
    case 'soft':
      return 'none';
    case 'none':
      return 'none';
  }
}

export function increaseCardRadius(
  cardRadius: SiteThemeCardRadius,
): SiteThemeCardRadius {
  switch (cardRadius) {
    case 'sharp':
      return 'soft';
    case 'soft':
      return 'rounded';
    case 'rounded':
      return 'extra_rounded';
    case 'extra_rounded':
      return 'extra_rounded';
  }
}

export function decreaseCardRadius(
  cardRadius: SiteThemeCardRadius,
): SiteThemeCardRadius {
  switch (cardRadius) {
    case 'extra_rounded':
      return 'rounded';
    case 'rounded':
      return 'soft';
    case 'soft':
      return 'sharp';
    case 'sharp':
      return 'sharp';
  }
}

export function increaseSpacingScale(
  spacingScale: SiteThemeSpacingScale,
): SiteThemeSpacingScale {
  switch (spacingScale) {
    case 'compact':
      return 'normal';
    case 'normal':
      return 'spacious';
    case 'spacious':
      return 'spacious';
  }
}

export function decreaseSpacingScale(
  spacingScale: SiteThemeSpacingScale,
): SiteThemeSpacingScale {
  switch (spacingScale) {
    case 'spacious':
      return 'normal';
    case 'normal':
      return 'compact';
    case 'compact':
      return 'compact';
  }
}

export function getThemePresentation(theme: SiteTheme): ResolvedSiteThemePresentation {
  if (!theme.presentation) return defaultThemePresentation;

  return {
    ...defaultThemePresentation,
    ...theme.presentation,
    cardShadow: resolveCardShadow(theme.presentation.cardStyle, theme.presentation.cardShadow),
    borderEmphasis: resolveBorderEmphasis(theme.presentation.borderEmphasis),
    cardRadius: resolveCardRadius(theme.presentation.cardRadius),
    spacingScale: resolveSpacingScale(theme.presentation.spacingScale),
  };
}

export function getSpacingScaleValues(theme: SiteTheme): ResolvedSpacingScaleValues {
  const { spacingScale } = getThemePresentation(theme);
  return spacingScaleValues[spacingScale];
}

export function getCardShadowStyle(theme: SiteTheme): string | undefined {
  const { cardShadow } = getThemePresentation(theme);

  switch (cardShadow) {
    case 'soft':
      return `0 14px 32px -24px ${theme.tokens.primary}18`;
    case 'medium':
      return `0 18px 40px -24px ${theme.tokens.primary}26`;
    case 'strong':
      return `0 24px 56px -28px ${theme.tokens.primary}34`;
    case 'none':
      return undefined;
  }
}

export function getCardBorderStyle(theme: SiteTheme): {
  borderColor: string;
  borderWidth: string;
} {
  const { borderEmphasis } = getThemePresentation(theme);

  switch (borderEmphasis) {
    case 'none':
      return {
        borderColor: 'transparent',
        borderWidth: '0px',
      };
    case 'soft':
      return {
        borderColor: `${theme.tokens.border}b3`,
        borderWidth: '1px',
      };
    case 'normal':
      return {
        borderColor: theme.tokens.border,
        borderWidth: '1px',
      };
    case 'strong':
      return {
        borderColor: `${theme.tokens.primary}2e`,
        borderWidth: '1.5px',
      };
  }
}

export function getCardRadiusStyle(theme: SiteTheme): {
  borderRadius: string;
} {
  const { cardRadius } = getThemePresentation(theme);

  switch (cardRadius) {
    case 'sharp':
      return { borderRadius: '0.75rem' };
    case 'soft':
      return { borderRadius: '1rem' };
    case 'rounded':
      return { borderRadius: '1.5rem' };
    case 'extra_rounded':
      return { borderRadius: '2rem' };
  }
}

export function getSectionSpacingStyle(theme: SiteTheme): {
  paddingTop: string;
  paddingBottom: string;
} {
  const { sectionPaddingY } = getSpacingScaleValues(theme);
  return {
    paddingTop: sectionPaddingY,
    paddingBottom: sectionPaddingY,
  };
}

export function getSectionGapStyle(theme: SiteTheme): {
  gap: string;
} {
  const { sectionGap } = getSpacingScaleValues(theme);
  return { gap: sectionGap };
}

export function getStackGapStyle(theme: SiteTheme): {
  gap: string;
} {
  const { stackGap } = getSpacingScaleValues(theme);
  return { gap: stackGap };
}

export function getCardPaddingStyle(theme: SiteTheme): {
  padding: string;
} {
  const { cardPadding } = getSpacingScaleValues(theme);
  return { padding: cardPadding };
}
